/** XeAgents.ai**/
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useTranslation } from 'react-i18next';
import { CampaignCard } from "@/components/CampaignCard";
import { Status } from "@/components/StatusBadge";
import { CreateCampaignDialog } from "@/components/CreateCampaignDialog";
import { EditCampaignDialog } from "@/components/EditCampaignDialog";
import { CampaignDetailsDialog } from "@/components/CampaignDetailsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Target, Play, CheckCircle2, XCircle, Clock, Trash2, RotateCcw } from "lucide-react";
import { parseDate } from "@/lib/date-format";
import { Card } from "@/components/ui/card";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";
import {
  CAMPAIGN_FINISHED_SUCCESS_STATUS,
  CAMPAIGN_FINISHED_FAILURE_STATUSES,
  isFinishedCampaignStatus,
  campaignListNeedsPolling,
} from "@shared/campaign-status";

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  totalContacts: number;
  completedCalls: number;
  successfulCalls: number;
  deletedAt?: string | null;
  phoneNumberId?: string | null;
  agentId: string;
  voiceId?: string;
  goal?: string;
  script?: string;
  scheduleEnabled?: boolean;
  scheduleTimeStart?: string;
  scheduleTimeEnd?: string;
  scheduleDays?: string[];
  scheduleTimezone?: string;
  scheduledFor?: string | null;
  config?: { failureReason?: string } | null;
}

const CAMPAIGN_SKELETON_COUNT = 6; // divisible by every grid breakpoint (1/2/3 cols) - no partial row

function CampaignGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: CAMPAIGN_SKELETON_COUNT }).map((_, i) => (
        <Card key={i} className="p-6 animate-pulse">
          <div className="h-6 bg-muted rounded w-3/4 mb-4" />
          <div className="h-4 bg-muted rounded w-full mb-2" />
          <div className="h-4 bg-muted rounded w-2/3" />
        </Card>
      ))}
    </div>
  );
}

export default function Campaigns() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [viewingCampaign, setViewingCampaign] = useState<Campaign | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active");
  const [restoringCampaignId, setRestoringCampaignId] = useState<string | null>(null);

  const { data: campaigns = [], isLoading } = useQuery<Campaign[]>({
    queryKey: ["/api/campaigns"],
    // Poll while anything is actually running so a stuck "Running" badge can never survive
    // longer than one interval without a manual refresh; stop as soon as nothing is running.
    refetchInterval: (query) => {
      const data = query.state.data as Campaign[] | undefined;
      return campaignListNeedsPolling(data ?? []) ? 5000 : false;
    },
  });

  const { data: deletedCampaigns = [], isLoading: deletedLoading } = useQuery<Campaign[]>({
    queryKey: ["/api/campaigns/deleted"],
  });

  const restoreMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      setRestoringCampaignId(campaignId);
      const res = await apiRequest("PATCH", `/api/campaigns/${campaignId}/restore`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns/deleted"] });
      toast({ title: t('campaigns.restoredSuccess') });
      setRestoringCampaignId(null);
    },
    onError: (error: any) => {
      toast({
        title: t('campaigns.restoreFailed'),
        description: error.message || t('common.tryAgain'),
        variant: "destructive",
      });
      setRestoringCampaignId(null);
    },
  });

  const activeStatusCampaigns = campaigns.filter((campaign) => !isFinishedCampaignStatus(campaign.status));
  const completedStatusCampaigns = campaigns.filter((campaign) => campaign.status === CAMPAIGN_FINISHED_SUCCESS_STATUS);
  const failedStatusCampaigns = campaigns.filter((campaign) => CAMPAIGN_FINISHED_FAILURE_STATUSES.includes(campaign.status));

  const matchesSearch = (campaign: Campaign) =>
    campaign.name.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredCampaigns = activeStatusCampaigns.filter((campaign) => {
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter;
    return matchesSearch(campaign) && matchesStatus;
  });

  const filteredCompletedCampaigns = completedStatusCampaigns.filter(matchesSearch);
  const filteredFailedCampaigns = failedStatusCampaigns.filter(matchesSearch);
  const filteredDeletedCampaigns = deletedCampaigns.filter(matchesSearch);

  const activePagination = usePagination(filteredCampaigns, 9);
  const completedPagination = usePagination(filteredCompletedCampaigns, 9);
  const failedPagination = usePagination(filteredFailedCampaigns, 9);
  const deletedPagination = usePagination(filteredDeletedCampaigns, 9);

  // ✅ FIX: campaigns.status is never 'in_progress' - the server only ever writes 'running'.
  // Counting 'in_progress' here always evaluated to 0, which is why this tile showed "0 Active"
  // even while campaigns were genuinely running.
  const activeCount = campaigns.filter(c => c.status === 'running').length;
  const completedCount = completedStatusCampaigns.length;
  const pendingCount = campaigns.filter(c => c.status === 'pending' || c.status === 'scheduled').length;
  const deletedCount = deletedCampaigns.length;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 via-orange-100/50 to-amber-50 dark:from-orange-950/40 dark:via-orange-900/30 dark:to-amber-950/40 border border-orange-100 dark:border-orange-900/50 p-6 md:p-8">
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-500/25">
              <Target className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('campaigns.title')}</h1>
              <p className="text-muted-foreground mt-0.5">{t('campaigns.description')}</p>
            </div>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg"
            data-testid="button-create-campaign"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('campaigns.createNew')}
          </Button>
        </div>

        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-orange-100/50 dark:border-orange-800/30">
            <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">{campaigns.length}</div>
            <div className="text-orange-600/70 dark:text-orange-400/70 text-sm">{t('campaigns.totalCampaigns')}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-emerald-100/50 dark:border-emerald-800/30">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{activeCount}</div>
              <Play className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
            </div>
            <div className="text-emerald-600/70 dark:text-emerald-400/70 text-sm">{t('campaigns.status.active')}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-blue-100/50 dark:border-blue-800/30">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{completedCount}</div>
              <CheckCircle2 className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            </div>
            <div className="text-blue-600/70 dark:text-blue-400/70 text-sm">{t('campaigns.status.completed')}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-amber-100/50 dark:border-amber-800/30">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{pendingCount}</div>
              <Clock className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            </div>
            <div className="text-amber-600/70 dark:text-amber-400/70 text-sm">{t('campaigns.status.pending')}</div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active-campaigns">
              {t('campaigns.activeCampaigns')} ({activeStatusCampaigns.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed-campaigns">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {t('campaigns.completedCampaigns')} ({completedStatusCampaigns.length})
            </TabsTrigger>
            <TabsTrigger value="failed" data-testid="tab-failed-campaigns">
              <XCircle className="h-4 w-4 mr-1" />
              {t('campaigns.failedCampaigns')} ({failedStatusCampaigns.length})
            </TabsTrigger>
            <TabsTrigger value="deleted" data-testid="tab-deleted-campaigns">
              <Trash2 className="h-4 w-4 mr-1" />
              {t('campaigns.deletedCampaigns')} ({deletedCount})
            </TabsTrigger>
          </TabsList>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto sm:flex-1 sm:justify-end">
            <div className="relative w-full sm:w-auto sm:min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('campaigns.searchPlaceholder')}
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="input-search-campaigns"
              />
            </div>
            {activeTab === "active" && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="select-filter-status">
                  <SelectValue placeholder={t('campaigns.filterByStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('campaigns.allStatus')}</SelectItem>
                  <SelectItem value="pending">{t('campaigns.status.pending')}</SelectItem>
                  <SelectItem value="running">{t('campaigns.status.active')}</SelectItem>
                  <SelectItem value="scheduled">{t('campaigns.status.scheduled')}</SelectItem>
                  <SelectItem value="paused">{t('campaigns.status.paused')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <TabsContent value="active" className="space-y-4">
          {isLoading ? (
            <CampaignGridSkeleton />
          ) : campaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <div className="relative">
                <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-orange-500/10 flex items-center justify-center">
                  <Target className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('campaigns.noActiveCampaigns')}</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {t('campaigns.getStartedCreate')}
                </p>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-gradient-to-r from-orange-600 to-amber-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('campaigns.createFirstCampaign')}
                </Button>
              </div>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <p className="text-muted-foreground">{t('campaigns.noMatchingCampaigns')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activePagination.paginatedItems.map((campaign) => {
                  const successRate = campaign.completedCalls > 0
                    ? ((campaign.successfulCalls / campaign.completedCalls) * 100).toFixed(1)
                    : undefined;

                  // Format scheduled time for display
                  const scheduledDate = parseDate(campaign.scheduledFor);
                  const scheduleDisplay = scheduledDate && campaign.status === 'scheduled'
                    ? scheduledDate.toLocaleString('en-US', {
                        timeZone: campaign.scheduleTimezone || 'UTC',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      }) + (campaign.scheduleTimezone ? ` (${campaign.scheduleTimezone.split('/').pop()?.replace('_', ' ')})` : '')
                    : undefined;

                  return (
                    <CampaignCard
                      key={campaign.id}
                      id={campaign.id}
                      name={campaign.name}
                      type={campaign.type}
                      status={campaign.status as Status}
                      totalContacts={campaign.totalContacts}
                      completedCalls={campaign.completedCalls}
                      failureReason={campaign.config?.failureReason}
                      successRate={successRate ? parseFloat(successRate) : undefined}
                      schedule={scheduleDisplay}
                      onView={() => setLocation(`/app/campaigns/${campaign.id}`)}
                      onEdit={() => setEditingCampaign(campaign)}
                    />
                  );
                })}
              </div>
              <DataPagination
                currentPage={activePagination.currentPage}
                totalPages={activePagination.totalPages}
                totalItems={activePagination.totalItems}
                itemsPerPage={activePagination.itemsPerPage}
                onPageChange={activePagination.handlePageChange}
                onItemsPerPageChange={activePagination.handleItemsPerPageChange}
                itemsPerPageOptions={[9, 18, 27]}
                data-testid="pagination-active-campaigns"
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {isLoading ? (
            <CampaignGridSkeleton />
          ) : completedStatusCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <div className="relative">
                <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('campaigns.noCompletedCampaigns')}</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('campaigns.completedCampaignsDesc')}
                </p>
              </div>
            </div>
          ) : filteredCompletedCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <p className="text-muted-foreground">{t('campaigns.noMatchingCompleted')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedPagination.paginatedItems.map((campaign) => {
                  const successRate = campaign.completedCalls > 0
                    ? ((campaign.successfulCalls / campaign.completedCalls) * 100).toFixed(1)
                    : undefined;

                  return (
                    <CampaignCard
                      key={campaign.id}
                      id={campaign.id}
                      name={campaign.name}
                      type={campaign.type}
                      status={campaign.status as Status}
                      totalContacts={campaign.totalContacts}
                      completedCalls={campaign.completedCalls}
                      failureReason={campaign.config?.failureReason}
                      successRate={successRate ? parseFloat(successRate) : undefined}
                      onView={() => setLocation(`/app/campaigns/${campaign.id}`)}
                      onEdit={() => setEditingCampaign(campaign)}
                    />
                  );
                })}
              </div>
              <DataPagination
                currentPage={completedPagination.currentPage}
                totalPages={completedPagination.totalPages}
                totalItems={completedPagination.totalItems}
                itemsPerPage={completedPagination.itemsPerPage}
                onPageChange={completedPagination.handlePageChange}
                onItemsPerPageChange={completedPagination.handleItemsPerPageChange}
                itemsPerPageOptions={[9, 18, 27]}
                data-testid="pagination-completed-campaigns"
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="failed" className="space-y-4">
          {isLoading ? (
            <CampaignGridSkeleton />
          ) : failedStatusCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <div className="relative">
                <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('campaigns.noFailedCampaigns')}</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('campaigns.failedCampaignsDesc')}
                </p>
              </div>
            </div>
          ) : filteredFailedCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <p className="text-muted-foreground">{t('campaigns.noMatchingFailed')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {failedPagination.paginatedItems.map((campaign) => {
                  const successRate = campaign.completedCalls > 0
                    ? ((campaign.successfulCalls / campaign.completedCalls) * 100).toFixed(1)
                    : undefined;

                  return (
                    <CampaignCard
                      key={campaign.id}
                      id={campaign.id}
                      name={campaign.name}
                      type={campaign.type}
                      status={campaign.status as Status}
                      totalContacts={campaign.totalContacts}
                      completedCalls={campaign.completedCalls}
                      failureReason={campaign.config?.failureReason}
                      successRate={successRate ? parseFloat(successRate) : undefined}
                      onView={() => setLocation(`/app/campaigns/${campaign.id}`)}
                      onEdit={() => setEditingCampaign(campaign)}
                    />
                  );
                })}
              </div>
              <DataPagination
                currentPage={failedPagination.currentPage}
                totalPages={failedPagination.totalPages}
                totalItems={failedPagination.totalItems}
                itemsPerPage={failedPagination.itemsPerPage}
                onPageChange={failedPagination.handlePageChange}
                onItemsPerPageChange={failedPagination.handleItemsPerPageChange}
                itemsPerPageOptions={[9, 18, 27]}
                data-testid="pagination-failed-campaigns"
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="deleted" className="space-y-4">
          {deletedLoading ? (
            <CampaignGridSkeleton />
          ) : deletedCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <div className="relative">
                <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center">
                  <Trash2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('campaigns.noDeletedCampaigns')}</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('campaigns.permanentlyRemoved')}
                </p>
              </div>
            </div>
          ) : filteredDeletedCampaigns.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border border-dashed border-muted-foreground/25 p-12 text-center">
              <p className="text-muted-foreground">{t('campaigns.noMatchingDeleted')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deletedPagination.paginatedItems.map((campaign) => (
                  <Card key={campaign.id} className="p-6 relative overflow-hidden opacity-75 hover:opacity-100 transition-opacity" data-testid={`card-deleted-campaign-${campaign.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground truncate mb-1" data-testid="text-deleted-campaign-name">
                          {campaign.name}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-muted-foreground font-medium">{campaign.type}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-sm text-destructive">{t('campaigns.status.deleted')}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restoreMutation.mutate(campaign.id)}
                        disabled={restoringCampaignId === campaign.id}
                        data-testid={`button-restore-campaign-${campaign.id}`}
                      >
                        <RotateCcw className={`h-4 w-4 mr-1 ${restoringCampaignId === campaign.id ? 'animate-spin' : ''}`} />
                        {restoringCampaignId === campaign.id ? t('campaigns.restoring') : t('campaigns.actions.restore')}
                      </Button>
                    </div>

                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex justify-between gap-2">
                        <span>{t('campaigns.details.totalContacts')}</span>
                        <span className="font-medium">{campaign.totalContacts}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>{t('campaigns.completedCalls')}</span>
                        <span className="font-medium">{campaign.completedCalls}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              <DataPagination
                currentPage={deletedPagination.currentPage}
                totalPages={deletedPagination.totalPages}
                totalItems={deletedPagination.totalItems}
                itemsPerPage={deletedPagination.itemsPerPage}
                onPageChange={deletedPagination.handlePageChange}
                onItemsPerPageChange={deletedPagination.handleItemsPerPageChange}
                itemsPerPageOptions={[9, 18, 27]}
                data-testid="pagination-deleted-campaigns"
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      <CreateCampaignDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      <EditCampaignDialog
        campaign={editingCampaign as any}
        open={!!editingCampaign}
        onOpenChange={(open: boolean) => !open && setEditingCampaign(null)}
      />
      <CampaignDetailsDialog
        campaign={viewingCampaign}
        open={!!viewingCampaign}
        onOpenChange={(open: boolean) => !open && setViewingCampaign(null)}
      />
    </div>
  );
}
